import type { IDatapoint } from '@/interfaces/IDatapoint'
import type { IResetTarget, MessageReset } from '@/interfaces/gui'
import { formatInTimeZone } from 'date-fns-tz'
import { project } from '@/composables/useMqttConnection'
import { useTranslationsStore } from '@/stores/translations'

export function formatDatapoint(datapoint: IDatapoint, includeUnit: boolean = true): string {
  // TODO quickfix because of dateInput in not formatted correctly
  if (datapoint.formatted?.match(/^\d+$/) && datapoint.formatted.length === 13) {
    // Ensure value is converted to number for Date constructor
    const dateValue =
      typeof datapoint.value === 'string' ? parseInt(datapoint.value, 10) : datapoint.value
    const date = new Date(Number(dateValue))
    const timezone = project.value?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    return formatInTimeZone(date, timezone, 'dd.MM.yyyy')
  }

  // Check for type and process accordingly
  if (datapoint.type === 'bool') {
    if (typeof datapoint.value !== 'boolean') {
      throw new Error("Invalid datapoint: 'value' is missing or not a boolean.")
    }
    return datapoint.value ? translate('#active') : translate('#inactive')
  }

  if (datapoint.type === 'string') {
    if (typeof datapoint.value !== 'string') {
      throw new Error("Invalid datapoint: 'value' is missing or not a string.")
    }
    return translate(datapoint.value)
  }

  // quick fix for missing mask
  if (!('mask' in datapoint) && 'unit' in datapoint && datapoint.unit?.startsWith('#')) {
    datapoint.mask = '#'
  }

  // Check if value and mask are present for number types
  if (!('value' in datapoint && 'mask' in datapoint)) {
    return ''
  }

  if (typeof datapoint.value !== 'number') {
    throw new Error("Invalid datapoint: 'value' is missing or not a number.")
  }
  if (typeof datapoint.mask !== 'string') {
    throw new Error("Invalid datapoint: 'mask' is missing or not a string.")
  }

  // Format the value based on the mask
  const decimals = (datapoint.mask.split('.')[1] || '').length
  const formattedValue = Number(datapoint.value).toFixed(decimals)

  // Include unit if present and includeUnit is true
  const unit = includeUnit && datapoint.unit ? datapoint.unit : ''
  return `${formattedValue} ${translate(unit)}`.trim()
}

export function translate(key: string): string {
  const translationsStore = useTranslationsStore()
  return translationsStore.translate(key)
}

/**
 * Translates a message key and replaces placeholders with dptext and fixtext values.
 * Supports both single values and arrays for dptext and fixtext.
 *
 * @param key - The translation key (e.g., "#device_offline")
 * @param dptext - Optional dynamic datapoint text values, can be string/number or array
 * @param fixtext - Optional fixed text values, can be string/number or array
 * @returns The translated string with all placeholders replaced
 *
 * @example
 * translateMessage("#device_offline", undefined, ["AIO.1", 50])
 * // Translation: "Busteilnehmer {{f0}} (BA {{f1}}) nicht gefunden"
 * // Result: "Busteilnehmer AIO.1 (BA 50) nicht gefunden"
 */
export function translateMessage(
  key: string,
  dptext?: string | number | ReadonlyArray<string | number> | null | undefined,
  fixtext?: string | number | ReadonlyArray<string | number> | null | undefined,
): string {
  let translated = translate(key)

  // Replace dptext placeholders {{0}}, {{1}}, etc.
  if (dptext !== null && dptext !== undefined) {
    const dptextArray = Array.isArray(dptext) ? dptext : [dptext]
    dptextArray.forEach((value, index) => {
      const placeholder = `{{${index}}}`
      translated = translated.replace(
        new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
        String(value),
      )
    })
  }

  // Replace fixtext placeholders {{f0}}, {{f1}}, etc.
  if (fixtext !== null && fixtext !== undefined) {
    const fixtextArray = Array.isArray(fixtext) ? fixtext : [fixtext]
    fixtextArray.forEach((value, index) => {
      const placeholder = `{{f${index}}}`
      translated = translated.replace(
        new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
        String(value),
      )
    })
  }

  if (import.meta.env.DEV) {
    console.log(`Translating key: ${key}, dptext: ${dptext}, fixtext: ${fixtext} => ${translated}`)
  }

  return translated
}

export function numberToColor(input: number): string {
  // Convert signed integer to unsigned (handles negative values)
  const unsignedValue = input >>> 0

  // Extract RGBA components
  const r = (unsignedValue >> 16) & 0xff // Red component
  const g = (unsignedValue >> 8) & 0xff // Green component
  const b = unsignedValue & 0xff // Blue component
  const a = ((unsignedValue >> 24) & 0xff) / 255 // Alpha component (0-1 range)

  // Return as a CSS color string in RGBA format
  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`
}

export function colorToNumber(color: string): number {
  // Remove any leading # from hex string
  const hex = color.replace('#', '')

  // Parse the hex string to RGB components
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Combine into 32-bit number (full alpha)
  return (0xff << 24) | (r << 16) | (g << 8) | b
}

const Themes = {
  red: 0xffe53935,
  blue: 0xff6182b8,
  green: 0xff91b859,
  gray: 0xffaabfc9,
  yellow: 0xfff6a434,
  purple: 0xff7c4dff,
  cyan: 0xff39adb5,
  orange: 0xfff76d47,
  text: 0xff94a7b0, // Default color
} satisfies Record<string, number>

function stringToColor(input: string): string {
  input = input.trim().toLowerCase()
  let isLight = false

  // Check if the input starts with "light"
  if (input.startsWith('light')) {
    isLight = true
    input = input.substring(5) // Remove "light" from the input
  }

  // Fetch the color from Themes
  const baseColor = Themes[input as keyof typeof Themes] ?? Themes.text // Default to "text" color if not found
  let color = baseColor

  // Apply "light" alpha logic if needed
  if (isLight) {
    const rgbOnly = color & 0x00ffffff // Strip alpha channel
    color = 0x88000000 | rgbOnly // Add 53% alpha
  }

  // Convert the 32-bit integer to RGB string
  return numberToColor(color)
}

export function toColor(input: number | string): string {
  if (typeof input === 'number') {
    return numberToColor(input)
  }
  return stringToColor(input)
}

export function parseJSON<T>(input: string | object): T | null {
  try {
    // If input is already an object, return it directly
    if (typeof input === 'object' && input !== null) {
      // Validate that the object matches expected type T structure
      return input as T
    }

    // Ensure input is a string before parsing
    if (typeof input !== 'string') {
      console.warn('parseJSON received non-string input:', typeof input)
      return null
    }

    // Handle empty strings
    if (input.trim() === '') {
      return null
    }

    const parsed = JSON.parse(input)

    // Validate that parsed result is not null/undefined
    if (parsed === null || parsed === undefined) {
      return null
    }

    return parsed as T
  } catch (err) {
    // Log parsing errors for debugging
    console.warn('Failed to parse JSON:', err instanceof Error ? err.message : 'Unknown error')
    return null
  }
}

/**
 * Narrows a config object to the index-signature shape vue-json-pretty expects.
 * Our interfaces describe the same JSON but are not structurally assignable to
 * `Record<string, unknown>` because they declare no index signature.
 */
export function toJsonViewerData(value: object): Record<string, unknown> {
  return value as Record<string, unknown>
}

/**
 * Converts an icon code to an HTML-safe character reference.
 * @param iconCode - The icon code (can be with or without 'x' prefix)
 * @returns An HTML-safe string containing the character reference
 */
export function iconToHtml(iconCode: string | undefined | null): string {
  if (!iconCode) {
    return ''
  }

  try {
    // Remove 'x' prefix if present
    const cleanCode = iconCode.replace(/^x/i, '')

    // Validate that the code is a valid hex number
    if (!/^[0-9A-F]+$/i.test(cleanCode)) {
      console.warn(`Invalid icon code format: ${iconCode}`)
      return ''
    }

    // Convert hex to decimal and then to character
    const char = String.fromCodePoint(parseInt(cleanCode, 16))

    // Return the character directly since it will be used with v-html
    return char
  } catch (error) {
    console.warn(`Error converting icon code: ${iconCode}`, error)
    return ''
  }
}

/**
 * Flattens a message's `reset` configuration into the datapoint writes it
 * stands for, mirroring `DPM.ApplyReset` on the device:
 *
 *   - a string is a datapoint name and writes `true`
 *   - an object carries its own `value`, defaulting to `true`
 *   - a list holds any mix of the two and is applied in order
 *
 * A `!` prefix stays on the name. The device inverts it in `DPM.SetValue`, and
 * only for datapoints of type `bool` — stripping or inverting it here would
 * write the wrong value on everything else.
 *
 * Unusable entries are skipped rather than thrown on, so one broken target in a
 * list cannot swallow the others.
 */
export function normalizeReset(
  reset: MessageReset | undefined | null,
): { datapoint: string; value: string | number | boolean }[] {
  if (reset === undefined || reset === null) {
    return []
  }

  if (Array.isArray(reset)) {
    return reset.flatMap((entry) => normalizeReset(entry as MessageReset))
  }

  if (typeof reset === 'string') {
    const datapoint = reset.trim()
    if (!datapoint) {
      console.warn('Reset entry without a datapoint - skipped')
      return []
    }
    return [{ datapoint, value: true }]
  }

  if (typeof reset === 'object') {
    const target = reset as IResetTarget
    const datapoint = typeof target.datapoint === 'string' ? target.datapoint.trim() : ''
    if (!datapoint) {
      console.warn('Reset entry without a datapoint - skipped', reset)
      return []
    }
    return [{ datapoint, value: target.value ?? true }]
  }

  console.warn('Unsupported reset configuration - skipped', reset)
  return []
}
